/* eslint-disable no-console */
import {InfoCircleOutlined} from '@ant-design/icons';
import {Col, Form, Select} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React, {useState, useEffect, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';

import {ReactComponent as YearCalender} from '../../assets/yearCalender.svg';
import {CommonErrors, TF} from '../../constants/Common';
import InformativeText from '../../constants/InformativeText';
import {sortBy} from '../../shared/commonFunctions';
import NumberInput from '../../shared/NumberInput';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import RegEx from '../../utils/FormUtils/RegEx';
import {yearSelect} from '../../utils/FormUtils/yearSelect';
import Loader from '../Loader/Loader';
import {MYConfig, MYData} from '../MonthlyYearly/Interface/common';
import MonthlyYearlyDrawerForm from '../MonthlyYearly/MonthlyYearlyDrawerForm';

import {yearKeyLabel} from './Constants/IP/commonKeyLabel';
import {GhKey, GhLabel} from './Constants/Products/GhKeyLabel';
import {unitTypes} from './interface/unitChange';
import changeUnit from './Utils/changeUnit';

const GhForm: React.FC<{
  rawMaterialFilter: any;
  data: any;
  currentStep?: number;
  setFormSelectedYear: React.Dispatch<React.SetStateAction<string>>;
  setIsFormLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isFormLoading: boolean;
}> = ({
  rawMaterialFilter,
  data,
  currentStep,
  setFormSelectedYear,
  setIsFormLoading,
  isFormLoading
}) => {
  const FormInstance = useFormInstance();
  const [searchParam] = useSearchParams();
  const {getFieldValue, setFields} = FormInstance;
  const {productFilter} = rawMaterialFilter;
  const ghInfo = InformativeText?.Product?.sold_products;
  const companyUnits = useCompanyStore.getState().preUnits;

  const yearFilter = yearSelect();
  const [productState, setProductState] = useState<any>(unitTypes.WEIGHT);

  useEffect(() => {
    setProductState(
      productFilter?.find((item: any) => {
        return (
          item.value ===
          (getFieldValue(GhKey.PRODUCT_SOLD) ?? data?.[GhKey.PRODUCT_SOLD])
        );
      })
    );
  }, [getFieldValue(GhKey.PRODUCT_SOLD)]);

  const monYear: MYConfig[] = useMemo(() => {
    return [
      {
        mainTitle: {
          key: GhKey.PRODUCT_SOLD,
          title: GhLabel.PRODUCT_SOLD
        },
        unit: changeUnit(productState?.state)
      }
    ];
  }, [productState]);

  return (
    <>
      {isFormLoading ? (
        <Loader />
      ) : (
        <>
          {currentStep === 0 && (
            <>
              <Col className="gutter-row">
                <Form.Item
                  name={yearKeyLabel.key}
                  label={yearKeyLabel.label}
                  labelCol={{span: 24}}
                  initialValue={searchParam.get('selectedYear')}
                >
                  <Select
                    placeholder={yearKeyLabel.label}
                    options={yearFilter}
                    suffixIcon={<YearCalender />}
                    disabled={data?._id !== undefined}
                    onChange={(e) => {
                      setFormSelectedYear(e);
                      setIsFormLoading(true);

                      if (getFieldValue(GhKey.PRODUCT_ID)) {
                        setFields([
                          {
                            name: GhKey.PRODUCT_ID,
                            errors: ['Please select product again'],
                            value: undefined
                          }
                        ]);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row">
                <Form.Item
                  name={GhKey.PRODUCT_ID}
                  rules={[
                    {
                      required: true,
                      message: CommonErrors.mandatory(GhLabel.PRODUCT_ID)
                    }
                  ]}
                  label={GhLabel.PRODUCT_ID}
                  labelCol={{span: 24}}
                >
                  <Select
                    disabled={data !== undefined}
                    className="select"
                    placeholder={`Choose ${GhLabel.PRODUCT_ID}`}
                    options={sortBy(productFilter, 'label', 'asc')}
                    onChange={(_, option: any) => {
                      setProductState(option);
                    }}
                  />
                </Form.Item>
              </Col>
            </>
          )}
          {currentStep === 1 && (
            <>
              <MonthlyYearlyDrawerForm
                config={monYear}
                data={getFieldValue(TF) as MYData[]}
              />
              <Col className="gutter-row">
                <Form.Item
                  name={GhKey.GH_PER_PRODUCT}
                  rules={[
                    {
                      pattern: RegEx.noZeroNoNegative,
                      message: CommonErrors.nonDecimalNonNegative
                    }
                  ]}
                  label={GhLabel.GH_PER_PRODUCT}
                  labelCol={{span: 24}}
                  tooltip={{
                    icon: <InfoCircleOutlined />,
                    title: ghInfo?.GHG_per_product
                  }}
                >
                  <NumberInput
                    placeholder={`Enter ${GhLabel.GH_PER_PRODUCT}`}
                    addonAfter={companyUnits.weight}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row">
                <Form.Item
                  name={GhKey.GH_PERCENT}
                  rules={[
                    {
                      pattern: RegEx.percentage,
                      message: CommonErrors.percentageError
                    }
                  ]}
                  label={GhLabel.GH_PERCENT}
                  labelCol={{span: 24}}
                  tooltip={{
                    icon: <InfoCircleOutlined />,
                    title: ghInfo?.GHG_percent
                  }}
                >
                  <NumberInput placeholder={`Enter ${GhLabel.GH_PERCENT}`} />
                </Form.Item>
              </Col>
            </>
          )}
        </>
      )}
    </>
  );
};

export default GhForm;
